import { LOCATION_PERMISSION_ERRORS } from '@/constants/amc';
import { useState, useCallback, useEffect } from 'react';

type Location = {
  lat: number;
  lng: number;
} | null;

const useGeolocation = () => {
  const [location, setLocation] = useState<Location>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);

  const getLocation = useCallback(() => {
    const handleSuccess = (position: GeolocationPosition) => {
      setLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setError(null);
      setIsLoading(false);
    };

    const handleError = (error: GeolocationPositionError) => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setError(LOCATION_PERMISSION_ERRORS.PERMISSION_DENIED);
          break;
        case error.POSITION_UNAVAILABLE:
          setError(LOCATION_PERMISSION_ERRORS.POSITION_UNAVAILABLE);
          break;
        case error.TIMEOUT:
          setError(LOCATION_PERMISSION_ERRORS.TIMEOUT);
          break;
        default:
          setError(LOCATION_PERMISSION_ERRORS.DEFAULT);
          break;
      }
      setIsLoading(false);
    };

    if ('geolocation' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted' || result.state === 'prompt') {
          setIsLoading(true);
          navigator.geolocation.getCurrentPosition(handleSuccess, handleError);
        } else if (result.state === 'denied') {
          setError(LOCATION_PERMISSION_ERRORS.PERMISSION_DENIED);
        }
      });
    } else {
      setError(LOCATION_PERMISSION_ERRORS.BROWSER_NO_SUPPORT);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null;
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
          permissionStatus = status;
          setPermissionState(status.state);

          status.onchange = () => {
            console.log('Permission changed:', status.state);
            setPermissionState(status.state);
          };
        })
        .catch(() => {
          console.log('Not supported for this browser');
        });
    }

    return () => {
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  return { location, error, isLoading, getLocation, permissionState };
};

export default useGeolocation;
